import { supabase } from './supabase';
import { FULL_NAICS_CODES } from './naics-data';
import { NAICS_RELATIONSHIPS } from './taxonomy/naics-relationships';

/**
 * Dashboard facet sources — the distinct agencies and NAICS codes that actually
 * exist in the OPEN opportunity pool, with counts. These power the smart Agency
 * filter and the smart NAICS selector so the UI only ever offers filters that
 * return results. The pool is small (a few dozen agencies, a few hundred NAICS),
 * so we fetch the full list once and search client-side.
 */

export interface AgencyFacet {
  agency: string;
  count: number;
}

export interface NaicsFacet {
  code: string;
  count: number;
  title?: string;
}

// Code -> human title, merged from the local NAICS datasets. Not exhaustive
// (federal NAICS has ~1000 codes; we only carry the common federal ones), so a
// missing title just means the option shows the bare code — still selectable.
const NAICS_TITLES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const n of NAICS_RELATIONSHIPS) {
    if (n.code && n.title) map[n.code] = n.title;
  }
  for (const n of FULL_NAICS_CODES as Array<{ code: string; title: string }>) {
    if (n.code && n.title && !map[n.code]) map[n.code] = n.title;
  }
  return map;
})();

export function naicsTitle(code: string): string | undefined {
  return NAICS_TITLES[code];
}

export async function fetchAgencyFacets(): Promise<AgencyFacet[]> {
  const { data, error } = await supabase.rpc('list_contract_agencies', {
    p_search: null,
    p_limit: 500,
  });
  if (error) {
    console.warn('Agency facets unavailable:', error.message);
    return [];
  }
  return (data || []).map((r: any) => ({ agency: r.agency as string, count: Number(r.n) }));
}

export async function fetchNaicsFacets(): Promise<NaicsFacet[]> {
  const { data, error } = await supabase.rpc('list_contract_naics', {
    p_search: null,
    p_limit: 1000,
  });
  if (error) {
    console.warn('NAICS facets unavailable:', error.message);
    return [];
  }
  return (data || []).map((r: any) => ({
    code: r.naics_code as string,
    count: Number(r.n),
    title: naicsTitle(r.naics_code as string),
  }));
}
